import { useConfig } from './ConfigContext';
import { useAuth } from '../auth/AuthContext';
import {
  stage2ConduitBootstrapRequest,
  stage2MigrationRequest,
  type Stage2MacRecoveryRequest,
} from './stage2Migration';

export interface EdgeDisk {
  mount: string;
  label: string;
  usedGB: number;
  totalGB: number;
  /**
   * Bytes actually available, in GB. Not `totalGB - usedGB`: ext4 reserves ~5%
   * of the filesystem for root, counted in total but never available. Optional
   * because heartbeats from edges older than 2026-07-28 don't carry it.
   */
  freeGB?: number;
  /** 0-100, matching `df`'s Use% (used / (used + free)). */
  pct: number;
  /** true for non-root real block devices (recordings / external data mount). */
  external: boolean;
}

export interface EdgeMetrics {
  /** ISO timestamp when this sample was taken on the edge. */
  at: string;
  /** 0-100 */
  cpuPct: number;
  load1: number;
  cores: number;
  memUsedMB: number;
  memTotalMB: number;
  /** TCP round-trip (ms) from the edge to the frps relay — the hop app off-LAN traffic traverses. null if unmeasured. */
  frpsRttMs?: number | null;
  /** Box's primary LAN IPv4 (e.g. 10.0.0.27) for local SSH/access. null if undetected. */
  lanIp?: string | null;
  /** Includes the root filesystem and any external/recordings drive. */
  disks: EdgeDisk[];
}

export interface EdgeInfo {
  edgeId?: string;
  /** Frozen server-side platform classification; never inferred from OS text. */
  securityPlatform?: 'linux' | 'darwin';
  hostname?: string;
  version?: string;
  /** Human-readable OS version of the box, e.g. "macOS 15.1" / "Ubuntu 24.04.1 LTS". */
  osVersion?: string;
  /** When the edge last reported its OS version. */
  osVersionAt?: string;
  /** Latest resource sample reported with the heartbeat. */
  metrics?: EdgeMetrics;
  currentSha?: string;
  currentShaShort?: string;
  currentShaSubject?: string;
  currentShaCommittedAt?: string;
  currentBranch?: string;
  lastDeployAt?: string;
  lastDeployReason?: 'deploy' | 'rollback' | 'heartbeat' | 'startup' | string;
  lastHeartbeatAt?: string;
  lastPublicIp?: string;
  lastPublicIpAt?: string;
  publicIp?: string;
  pairedHomes?: string[];
  /** Integration keys this edge last reported (e.g. hue, tesla, seam, dyson). */
  integrations?: string[];
  /** When the edge last reported its integration set. */
  integrationsAt?: string;
  firstRegisteredAt?: string;
  lastRegisteredAt?: string;
  /** Legacy alias kept for compatibility — newer rows use firstRegisteredAt/lastRegisteredAt. */
  registeredAt?: string;
  release?: EdgeReleaseStatus;
}

export interface EdgeReleaseHistoryEvent {
  eventKey: string;
  schemaVersion: 1;
  eventId: string;
  at: string;
  kind: 'assigned' | 'paused' | 'applying' | 'deployed' | 'healthy'
    | 'rollback' | 'quarantined' | 'repair_required';
  source: 'assignment' | 'user-report' | 'joined-evidence';
  runtimeSha: string;
  activeSha: string | null;
  assignmentRevision: number | null;
  cohort: string | null;
  reason: string | null;
  failureCode: string | null;
}

export interface EdgeReleaseHistoryPage {
  events: EdgeReleaseHistoryEvent[];
  nextBefore: string | null;
}

export interface EdgeReleaseStatus {
  schemaVersion: 1;
  family: 'dedicated-runtime';
  desired: null | {
    revision: number;
    runtimeSha: string;
    controllerVersion: string | null;
    controlPlaneSha256: string | null;
    macAppVersion: string | null;
    cohort: string;
    paused: boolean;
    recoverySha: string | null;
  };
  installed: {
    runtimeSha: string | null;
    corroboratedRuntimeSha: string | null;
    controlPlaneSha256: string | null;
    macAppVersion: string | null;
  };
  contact: 'online' | 'offline';
  lastContactAt: string | null;
  lastUpdateCheckAt: string | null;
  transitionAgeSec: number | null;
  failedSha: string | null;
  state: 'unknown' | 'paused' | 'offline' | 'applying' | 'stalled' | 'healthy'
    | 'behind' | 'rollback' | 'quarantined' | 'repair_required';
  failureCode: string | null;
}

export interface ReleaseManifest {
  sha: string;
  shaShort: string;
  subject: string;
  committedAt: string;
  builtAt: string;
  channel: string;
  tarballSha256: string;
  sizeBytes: number;
}

export interface LatestRelease {
  channel: string;
  manifest: ReleaseManifest;
}

interface Stage2MigrationCommandBase {
  homeId: string;
  edgeId: string;
  expiresAt: number;
  ttlSeconds: number;
}

export interface DirectStage2MigrationCommand extends Stage2MigrationCommandBase {
  blindConduitRequired: false;
  migrationInstallCommand: string;
}

export interface BrokerStage2MigrationPayload extends Stage2MigrationCommandBase {
  blindConduitRequired: true;
  brokerRecoveryPayload: string;
}

export type Stage2MigrationCommand =
  | DirectStage2MigrationCommand
  | BrokerStage2MigrationPayload;

export interface MacConduitBootstrapCommand {
  homeId: string;
  edgeId: string;
  expiresAt: number;
  ttlSeconds: number;
  bootstrapInstallCommand: string;
}

export interface Home {
  homeId: string;
  displayName: string;
  address?: string;
  ownerEmail?: string;
  region?: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  accountIds?: { test?: string; prod?: string };
  allowFullTeardown?: { test?: boolean; prod?: boolean };
  lastExecutionArn?: string;
  lastError?: string;
  /** Populated by list-homes — short summary of the edge box paired to this home. */
  edge?: EdgeInfo;
  /** Deprecated home (e.g. legacy 720FrontRd, superseded by a per-home account). Hidden by default in the list. */
  isLegacy?: boolean;
}

export interface ExecutionDetails {
  status?: string;
  startDate?: string;
  stopDate?: string;
  input?: unknown;
  output?: unknown;
  error?: string;
  cause?: string;
}

export interface ExecutionEvent {
  timestamp?: string;
  type?: string;
  stateName?: string;
  error?: string;
  cause?: string;
}

export interface ProvisioningStep {
  id: string;
  label: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  error?: string;
  cause?: string;
  recovery?: string;
}

export interface CreateHomeInput {
  homeId: string;
  displayName: string;
  address?: string;
  ownerEmail: string;
  region?: string;
  /** Optional: extra members to invite at creation time. Each gets a Memberships row + an invite email. */
  additionalMembers?: string[];
}

/** 'shared' = Standard (shared cloud environment); 'dedicated' = isolated per-home AWS account. */
export type RegistrationTier = 'shared' | 'dedicated';

export type RegistrationStatus =
  | 'pending_review'
  | 'approved_provisioning'
  | 'key_sent'
  | 'key_claimed'
  | 'paired'
  | 'rejected';

export interface SharedTierCapOverride {
  reason: string;
  reviewedBy: string;
  reviewedAt: string;
  maxHomes: number;
  reservedHomesBefore: number;
  reservedHomeIdsBefore: string[];
}

/** A self-registration request from mymanor.click/register (or the CLI/installer). */
export interface Registration {
  registrationId: string;
  email: string;
  name?: string;
  homeName?: string;
  tier: RegistrationTier;
  status: RegistrationStatus;
  address?: string;
  source: 'web' | 'cli' | 'installer';
  createdAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  /** Tier selected by the reviewer. Kept separately from the originally requested tier. */
  approvedTier?: RegistrationTier;
  /** Audit record written only when a reviewer bypasses the Standard one-home cap. */
  sharedTierCapOverride?: SharedTierCapOverride;
  rejectReason?: string;
  /** Set once approved — the home created (or reused) for this registration. */
  homeId?: string;
  keySentAt?: string;
  keyClaimedAt?: string;
  pairedAt?: string;
}

export type RegistrationApprovalInput = {
  tier: RegistrationTier;
  homeId: string;
  displayName?: string;
} & (
  | { overrideSharedTierCap: true; overrideReason: string }
  | { overrideSharedTierCap?: never; overrideReason?: never }
);

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function useApi() {
  const config = useConfig();
  const { idToken } = useAuth();

  const fetchJson = async <T,>(path: string, init?: RequestInit): Promise<T> => {
    const url = `${config.apiUrl.replace(/\/$/, '')}${path}`;
    const res = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...(init?.headers ?? {}),
      },
    });
    const text = await res.text();
    let body: unknown = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { raw: text };
    }
    if (!res.ok) {
      const errMsg =
        body && typeof body === 'object'
          ? ((body as { error?: unknown; message?: unknown }).error ??
              (body as { error?: unknown; message?: unknown }).message)
          : undefined;
      const errCode =
        body && typeof body === 'object' ? (body as { code?: unknown }).code : undefined;
      throw new ApiError(
        typeof errMsg === 'string' ? errMsg : `HTTP ${res.status}`,
        res.status,
        typeof errCode === 'string' ? errCode : undefined,
      );
    }
    return body as T;
  };

  return {
    concierge: (path = '', init?: RequestInit) => fetchJson<any>('/concierge' + path, init),
    listHomes: () => fetchJson<{ homes: Home[] }>('/homes'),
    getHome: (homeId: string) =>
      fetchJson<{
        home: Home;
        execution: ExecutionDetails | null;
        steps: ProvisioningStep[];
        history: ExecutionEvent[];
        edges: EdgeInfo[];
        edgeStatusUnavailable?: boolean;
      }>(`/homes/${encodeURIComponent(homeId)}`),
    retryHome: (homeId: string) =>
      fetchJson<{ homeId: string; status: string; executionArn: string }>(
        `/homes/${encodeURIComponent(homeId)}/retry`,
        { method: 'POST' },
      ),
    createHome: (input: CreateHomeInput) =>
      fetchJson<{
        homeId: string;
        status: string;
        provisionCommand: string;
        reused?: boolean;
        reusedFromHomeId?: string;
        message: string;
      }>('/homes', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    teardownHome: (homeId: string, stage: 'test' | 'prod' = 'test', mode: 'soft' | 'hard' = 'soft') =>
      fetchJson<{ homeId: string; status: string; mode: string; destroyCommand: string }>(
        `/homes/${encodeURIComponent(homeId)}/teardown?stage=${stage}&mode=${mode}`,
        { method: 'POST' },
      ),
    listIntegrations: (homeId: string, stage: 'test' | 'prod' = 'test') =>
      fetchJson<HomeIntegrations>(
        `/homes/${encodeURIComponent(homeId)}/integrations?stage=${stage}`,
      ),
    listMembers: (homeId: string) =>
      fetchJson<{ homeId: string; members: HomeMember[] }>(
        `/homes/${encodeURIComponent(homeId)}/members`,
      ),
    addMember: (homeId: string, input: { email: string; role: 'owner' | 'member' }) =>
      fetchJson<{ homeId: string; email: string; role: string; userId: string }>(
        `/homes/${encodeURIComponent(homeId)}/members`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    removeMember: (homeId: string, userId: string) =>
      fetchJson<{ homeId: string; userId: string; removed: boolean }>(
        `/homes/${encodeURIComponent(homeId)}/members/${encodeURIComponent(userId)}`,
        { method: 'DELETE' },
      ),
    getIntegration: (homeId: string, provider: string, stage: 'test' | 'prod' = 'test') =>
      fetchJson<IntegrationDetails>(
        `/homes/${encodeURIComponent(homeId)}/integrations/${encodeURIComponent(provider)}?stage=${stage}`,
      ),
    updateIntegration: (
      homeId: string,
      provider: string,
      fields: Record<string, string | null>,
      stage: 'test' | 'prod' = 'test',
    ) =>
      fetchJson<{ provider: string; updates: Array<{ key: string; action: string }> }>(
        `/homes/${encodeURIComponent(homeId)}/integrations/${encodeURIComponent(provider)}?stage=${stage}`,
        { method: 'PUT', body: JSON.stringify({ fields }) },
      ),
    listHomeCameras: (homeId: string) =>
      fetchJson<{ cameras: HomeCamera[] }>(`/homes/${encodeURIComponent(homeId)}/cameras`),
    upsertHomeCamera: (homeId: string, body: Partial<HomeCamera>) =>
      fetchJson<{ camera: HomeCamera }>(
        `/homes/${encodeURIComponent(homeId)}/cameras`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    deleteHomeCamera: (homeId: string, cameraId: string) =>
      fetchJson<{ ok: true }>(
        `/homes/${encodeURIComponent(homeId)}/cameras/${encodeURIComponent(cameraId)}`,
        { method: 'DELETE' },
      ),
    // Owner/super-admin only (issue #430). `replaceEdge` records explicit intent to
    // displace a DIFFERENT edge already paired to the home; re-pairing the same box
    // never needs it.
    mintPairingToken: (homeId: string, options: { replaceEdge?: boolean } = {}) =>
      fetchJson<{
        token: string;
        homeId: string;
        installCommand: string;
        installScriptUrl: string;
        registerUrl: string;
        expiresAt: number;     // unix seconds
        ttlSeconds: number;
      }>(
        `/pair/token/${encodeURIComponent(homeId)}`,
        {
          method: 'POST',
          ...(options.replaceEdge ? { body: JSON.stringify({ replaceEdge: true }) } : {}),
        },
      ),
    mintStage2MigrationCommand: async (
      homeId: string,
      edgeId: string,
      recovery?: Stage2MacRecoveryRequest,
    ): Promise<Stage2MigrationCommand> => {
      const request = stage2MigrationRequest(
        homeId,
        edgeId,
        recovery,
      );
      const blindConduitRequired = recovery?.schemaVersion === 3;
      const response = await fetchJson<Stage2MigrationCommandBase & {
        migrationInstallCommand?: unknown;
        brokerRecoveryPayload?: unknown;
        token?: unknown;
        installScriptUrl?: unknown;
        registerUrl?: unknown;
      }>(request.path, {
        method: 'POST',
        cache: 'no-store',
        body: JSON.stringify(request.body),
      });
      const nowSeconds = Math.floor(Date.now() / 1000);
      if (response.homeId !== homeId
          || response.edgeId !== edgeId
          || response.ttlSeconds !== 300
          || !Number.isSafeInteger(response.expiresAt)
          || response.expiresAt > nowSeconds + 330) {
        throw new ApiError('Invalid Stage 2 migration command response', 502);
      }
      if (response.expiresAt <= nowSeconds + 120) {
        throw new ApiError('Stage 2 migration command arrived outside its safe admission window', 409);
      }
      const responseKeys = Object.keys(response).sort().join(',');
      if (blindConduitRequired) {
        if (responseKeys !== 'brokerRecoveryPayload,edgeId,expiresAt,homeId,ttlSeconds'
            || typeof response.brokerRecoveryPayload !== 'string'
            || response.brokerRecoveryPayload.length < 1
            || response.brokerRecoveryPayload.length > 128 * 1024
            || response.migrationInstallCommand !== undefined
            || response.token !== undefined
            || response.installScriptUrl !== undefined
            || response.registerUrl !== undefined) {
          throw new ApiError('Invalid Darwin broker recovery payload response', 502);
        }
        return {
          homeId: response.homeId,
          edgeId: response.edgeId,
          expiresAt: response.expiresAt,
          ttlSeconds: response.ttlSeconds,
          blindConduitRequired: true,
          brokerRecoveryPayload: response.brokerRecoveryPayload,
        };
      }
      if (responseKeys !== [
        'edgeId',
        'expiresAt',
        'homeId',
        'installScriptUrl',
        'migrationInstallCommand',
        'registerUrl',
        'token',
        'ttlSeconds',
      ].join(',')
          || typeof response.migrationInstallCommand !== 'string'
          || response.migrationInstallCommand.length < 1
          || response.migrationInstallCommand.length > 128 * 1024
          || response.brokerRecoveryPayload !== undefined) {
        throw new ApiError('Invalid Stage 2 migration command response', 502);
      }
      // Deliberately discard the raw token and URLs at the API boundary. The
      // component receives only the complete one-shot command plus safe audit
      // metadata and can never accidentally render the token response fields.
      return {
        homeId: response.homeId,
        edgeId: response.edgeId,
        expiresAt: response.expiresAt,
        ttlSeconds: response.ttlSeconds,
        blindConduitRequired: false,
        migrationInstallCommand: response.migrationInstallCommand,
      };
    },
    mintMacConduitBootstrap: async (
      homeId: string,
      edgeId: string,
      projectionJson: string,
    ): Promise<MacConduitBootstrapCommand> => {
      const request = await stage2ConduitBootstrapRequest(projectionJson, homeId, edgeId);
      const response = await fetchJson<MacConduitBootstrapCommand & {
        bootstrapCapsule?: unknown;
        token?: unknown;
        migrationInstallCommand?: unknown;
        installScriptUrl?: unknown;
        registerUrl?: unknown;
      }>(request.path, {
        method: 'POST',
        cache: 'no-store',
        body: JSON.stringify(request.body),
      });
      const nowSeconds = Math.floor(Date.now() / 1000);
      if (Object.keys(response).sort().join(',')
            !== 'bootstrapInstallCommand,edgeId,expiresAt,homeId,ttlSeconds'
          || response.homeId !== homeId
          || response.edgeId !== edgeId
          || response.ttlSeconds !== 300
          || !Number.isSafeInteger(response.expiresAt)
          || response.expiresAt <= nowSeconds + 30
          || response.expiresAt > nowSeconds + 330
          || typeof response.bootstrapInstallCommand !== 'string'
          || response.bootstrapInstallCommand.length < 1
          || response.bootstrapInstallCommand.length > 128 * 1024
          || response.bootstrapCapsule !== undefined
          || response.token !== undefined
          || response.migrationInstallCommand !== undefined
          || response.installScriptUrl !== undefined
          || response.registerUrl !== undefined) {
        throw new ApiError('Invalid Mac conduit bootstrap response', 502);
      }
      // Return only the complete one-shot transaction plus safe audit fields.
      // Nested presigned URLs/capsule fields and all migration bearer material
      // are discarded at this boundary and can never be rendered separately.
      return {
        homeId: response.homeId,
        edgeId: response.edgeId,
        expiresAt: response.expiresAt,
        ttlSeconds: response.ttlSeconds,
        bootstrapInstallCommand: response.bootstrapInstallCommand,
      };
    },
    listEdges: async () => {
      const edges: EdgeInfo[] = [];
      let cursor: string | null = null;
      let pageCount = 0;
      do {
        const page: { edges: EdgeInfo[]; nextToken: string | null } = await fetchJson(
          `/edges?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        );
        if (!Array.isArray(page.edges) || (page.nextToken !== null && typeof page.nextToken !== 'string')) {
          throw new ApiError('Invalid edge fleet page', 502);
        }
        edges.push(...page.edges);
        cursor = page.nextToken;
        pageCount += 1;
        if (pageCount > 1000) throw new ApiError('Edge fleet pagination exceeded limit', 502);
      } while (cursor);
      edges.sort((a, b) => (b.lastHeartbeatAt ?? '').localeCompare(a.lastHeartbeatAt ?? ''));
      return { edges };
    },
    getLatestRelease: (channel = 'stable') =>
      fetchJson<LatestRelease>(`/releases/latest?channel=${encodeURIComponent(channel)}`),
    getEdgeReleaseHistory: (edgeId: string, before?: string) =>
      fetchJson<EdgeReleaseHistoryPage>(
        `/edges/${encodeURIComponent(edgeId)}/release/history?limit=20`
        + (before ? `&before=${encodeURIComponent(before)}` : ''),
        { cache: 'no-store' },
      ),
    listRegistrations: () =>
      fetchJson<{ registrations: Registration[] }>('/registrations'),
    approveRegistration: (
      registrationId: string,
      input: RegistrationApprovalInput,
    ) =>
      fetchJson<{ registrationId: string; status: string; homeId: string }>(
        `/registrations/${encodeURIComponent(registrationId)}/approve`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    rejectRegistration: (registrationId: string, reason: string) =>
      fetchJson<{ registrationId: string; status: string }>(
        `/registrations/${encodeURIComponent(registrationId)}/reject`,
        { method: 'POST', body: JSON.stringify({ reason }) },
      ),
    /** Sends (or re-sends — the previous link is invalidated) the one-time edge-key email. */
    sendRegistrationKey: (registrationId: string) =>
      fetchJson<{ registrationId: string; status: string; keySentAt?: string }>(
        `/registrations/${encodeURIComponent(registrationId)}/send-key`,
        { method: 'POST' },
      ),
  };
}

export interface IntegrationFieldDetail {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'email';
  required: boolean;
  encrypted?: boolean;
  placeholder?: string;
  /** Plain (String) value if present and not encrypted; null if encrypted or unset. */
  value: string | null;
  /** True if a value is stored in SSM (regardless of whether we can read it). */
  isSet: boolean;
}

export interface IntegrationDetails {
  provider: string;
  schema: { name: string; provider: string; edgeBased?: boolean };
  stage: string;
  accountId: string | null;
  fields: IntegrationFieldDetail[];
  warning?: string;
}

export interface HomeMember {
  userId: string;
  email: string;
  role: 'owner' | 'member';
  addedAt?: string;
  pending: boolean;
}

export interface ProviderIntegration {
  provider: string;
  fieldCount: number;
  fields: string[];
  lastModified?: string;
  paired?: boolean;
  hostname?: string;
  publicIp?: string;
  version?: string;
  registeredAt?: string;
}

export interface HomeIntegrations {
  homeId: string;
  stage: string;
  accountId: string | null;
  edgePaired?: boolean;
  edgeBasedProviders?: string[];
  providers: ProviderIntegration[];
  warning?: string;
}

export type CameraCodec = 'h264' | 'h265';

export interface HomeCamera {
  cameraId: string;
  label: string;
  role: string;
  rtspUrl: string;
  rtspSubUrl?: string;
  codec: CameraCodec;
  portrait?: boolean;
  ip?: string;
  zones?: Record<string, unknown>;
  /** The stored RTSP URLs carry a password. The API never returns it (URLs come back
   *  without user:pass); resubmitting the URL for the same host keeps the password. */
  rtspCredentialsSet?: boolean;
  addedAt: string;
  updatedAt: string;
}
